/* eslint-disable react-hooks/exhaustive-deps */
/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-03-19 19:58:17
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Field } from 'redux-form';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { Icon, Link } from '@shopify/polaris';
import { ExternalSmallMinor } from '@shopify/polaris-icons';

import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { RenderCheckbox } from 'src/components/views/common/forms/render-checkbox';
import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import UploadImage from './upload-image';

import { isRequiredValue, isValidUrlValue } from 'src/helpers/merchandising-v2-helper';
import { getThemePreviewUrl } from 'src/helpers';
import { convertImageToBase64 } from 'src/helpers';
import { MAX_FILE_SIZE, ACCEPT_FILE_IMAGE_TYPE } from 'src/constants/merchandising-v2';

type Props = {
  fields: Object,
  settings: {
    type: 'banner',
    position: Number,
    imgSrc: File | Object,
    link: String,
    openInNewTab: Boolean
  },
  indexStrategy: Number
};

type ComposedProps = Props & WithI18nProps;

const Attributes = (props: ComposedProps) => {
  const {
    i18n,
    settings = [],
    item,
    strategies,
    indexStrategy,
    updateField,
    mainTheme,
    formValues
  } = props;
  const mainThemeId = _.get(mainTheme, 'id', null);

  const [image, setImage] = useState();
  const [currentSize, setCurrentSize] = useState(0);

  const positionList = [
    { value: 0, label: 1 },
    { value: 1, label: 2 },
    { value: 2, label: 3 },
    { value: 3, label: 4 },
    { value: 4, label: 5 },
    { value: 5, label: 6 }
  ];

  const selectedPositions = settings.map(a => a.position);
  // remove in positionList
  const positions = positionList.filter(
    element =>
      !selectedPositions.includes(element.value) ||
      element.value === strategies[indexStrategy].position
  );

  const term = _.get(formValues, 'queries[0]', null);
  const collection = _.get(formValues, 'collection.handle', null);
  const previewUrl = getThemePreviewUrl(mainThemeId, term, collection);

  // Handle select image
  const onSelectImage = async files => {
    let source64 = null;
    if (files[0] && files[0].size <= MAX_FILE_SIZE) {
      source64 = await convertImageToBase64(files[0]);
    }
    setImage(files[0]);
    updateField(`${item}.imgSrc.url`, files[0]);
    updateField(`${item}.imgSrc.size`, files[0].size);
    updateField(`${item}.imgSrc.name`, files[0].name);
    updateField(`${item}.imgSrc.source64`, source64);
  };

  // Clear current image
  const removeImage = () => {
    setImage(null);
    updateField(`${item}.imgSrc`, null);
  };

  useEffect(() => {
    const currentImage = _.get(strategies[indexStrategy], 'imgSrc.url', null);
    if (currentImage) {
      setImage(currentImage);
      setCurrentSize(_.get(strategies[indexStrategy], 'imgSrc.size', 0));
    }
    const currPosition = _.get(strategies[indexStrategy], 'position', null);
    if (!currPosition && currPosition !== 0) {
      updateField(`${item}.position`, positions[0].value);
    }
  }, [strategies.length]);

  return (
    <div className="banner-settings" key={`attribute-banner-${indexStrategy}`}>
      <Field
        name={`${item}.position`}
        label={i18n.translate('AdminHTML.MerchandisingV2.BannerSettings.positionInputLabel')}
        component={RenderSelect}
        options={positions}
        defaultValue={positions[0].value}
        dataType="integer"
      />
      <Field
        name={`${item}.name`}
        label={i18n.translate('AdminHTML.MerchandisingV2.BannerSettings.nameInputLabel')}
        component={RenderTextField}
        dataType="string"
        validate={[isRequiredValue]}
        placeholder={i18n.translate(
          'AdminHTML.MerchandisingV2.BannerSettings.nameInputPlaceholder'
        )}
      />

      {/* Import image */}
      <div className="banner-image">
        <p style={{ marginBottom: '4px' }}>
          {i18n.translate('AdminHTML.MerchandisingV2.BannerSettings.imageInputLabel')}
        </p>
        <UploadImage
          name={'upload-image' + indexStrategy}
          allowMultiple={false}
          onSelectFile={onSelectImage}
          accept={ACCEPT_FILE_IMAGE_TYPE}
          fileTypes={ACCEPT_FILE_IMAGE_TYPE.split(', ')}
          defaultFile={image}
          key={`banner-image-${indexStrategy}`}
          removeImage={removeImage}
          defaultFileSize={currentSize}
        />
        <p>{i18n.translate('AdminHTML.MerchandisingV2.BannerSettings.imageInputDescription')}</p>
        {typeof image === 'string' && previewUrl && (
          <Link external url={previewUrl}>
            {i18n.translate('AdminHTML.MerchandisingV2.BannerSettings.imageRedirect')}
            <Icon source={ExternalSmallMinor} color={'interactive'}></Icon>
          </Link>
        )}
      </div>

      <Field
        name={`${item}].link`}
        component={RenderTextField}
        label={i18n.translate('AdminHTML.MerchandisingV2.BannerSettings.redirectLabel')}
        tooltip={i18n.translate('AdminHTML.MerchandisingV2.BannerSettings.redirectTooltip')}
        dataType="string"
        validate={[isRequiredValue, isValidUrlValue]}
        placeholder={i18n.translate('AdminHTML.MerchandisingV2.BannerSettings.redirectPlaceholder')}
      />

      <Field
        name={`${item}].openInNewTab`}
        component={RenderCheckbox}
        label={i18n.translate('AdminHTML.MerchandisingV2.BannerSettings.checkboxLabel')}
        defaultValue={true}
      />
    </div>
  );
};
export default withI18n()(Attributes);
